import type { Transaction } from "@/types";
import { ArrowDownToLine, Calendar, CreditCard, Wallet } from "lucide-react";

interface WithdrawalLogProps {
  withdrawals: Transaction[];
  title?: string;
  emptyMessage?: string;
}

const METHOD_LABELS: Record<string, { label: string; icon: typeof Wallet; color: string }> = {
  mobile_money: { label: "M-Pesa", icon: Wallet, color: "text-emerald-600 bg-emerald-50" },
  bank: { label: "Bank", icon: CreditCard, color: "text-blue-600 bg-blue-50" },
  paypal: { label: "PayPal", icon: Wallet, color: "text-purple-600 bg-purple-50" },
};

function formatCurrency(cents: number): string {
  return `$${(Math.abs(cents) / 100).toFixed(2)}`;
}

function formatDate(dateStr: string): string {
  const date = new Date(dateStr);
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function getMethodInfo(method?: string) {
  if (!method) return METHOD_LABELS["mobile_money"];
  const normalized = method.toLowerCase().replace(/[\s-]/g, "_");
  if (normalized.includes("bank")) return METHOD_LABELS["bank"];
  if (normalized.includes("paypal")) return METHOD_LABELS["paypal"];
  return METHOD_LABELS["mobile_money"];
}

export function WithdrawalLog({
  withdrawals,
  title = "Withdrawal History",
  emptyMessage = "No withdrawals yet",
}: WithdrawalLogProps) {
  const sorted = [...withdrawals].sort(
    (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime(),
  );

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
      <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-4">{title}</h3>

      {sorted.length === 0 ? (
        <p className="text-sm text-slate-400 text-center py-6">{emptyMessage}</p>
      ) : (
        <div className="space-y-3">
          {sorted.map((tx) => {
            const methodInfo = getMethodInfo(tx.method);
            const MethodIcon = methodInfo.icon;
            const statusColor =
              tx.status === "success"
                ? "text-emerald-600 bg-emerald-50"
                : tx.status === "pending"
                  ? "text-amber-600 bg-amber-50"
                  : "text-red-600 bg-red-50";

            return (
              <div
                key={tx.id}
                className="flex items-center justify-between rounded-xl border border-slate-100 bg-slate-50/50 p-3"
              >
                <div className="flex items-center gap-3">
                  <div
                    className={`flex h-10 w-10 items-center justify-center rounded-xl ${methodInfo.color}`}
                  >
                    <MethodIcon className="h-4 w-4" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-slate-700">{methodInfo.label}</p>
                    <div className="flex items-center gap-1 text-xs text-slate-400">
                      <Calendar className="h-3 w-3" />
                      {formatDate(tx.date)}
                    </div>
                  </div>
                </div>

                <div className="text-right">
                  <p className="text-sm font-semibold text-slate-700 tabular-nums">
                    -{formatCurrency(tx.amount)}
                  </p>
                  <span
                    className={`inline-block rounded-full px-2 py-0.5 text-[10px] font-semibold ${statusColor}`}
                  >
                    {tx.status}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
